import { useParams } from 'react-router-dom'
import { useSlideUpDialog } from '../../cart/useSlideUpDialog'
import { useCart } from '../../cart/useCart'
import { useCheckout } from '../../cart/useCheckout'
import { formatZARCents } from '../../lib/format'
import { MIN_ORDER_CENTS } from '../../cart/types'

interface Props {
  isOpen: boolean
  onClose: () => void
}

export function CartDrawer({ isOpen, onClose }: Props) {
  const { dialogRef, closeBtnRef, requestClose, handleCancel, handleDialogClick } =
    useSlideUpDialog(isOpen, onClose)
  const { lines, totalCents, setQuantity, removeItem } = useCart()
  const { slug } = useParams<{ slug: string }>()
  const { tableLabel, setTableLabel, checkout, isSubmitting, error } = useCheckout(slug ?? '')
  const belowMinimum = totalCents > 0 && totalCents < MIN_ORDER_CENTS

  return (
    <dialog
      ref={dialogRef}
      onClick={handleDialogClick}
      onCancel={handleCancel}
      className="w-full max-w-lg rounded-t-[var(--sheet-radius)] sm:rounded-[var(--sheet-radius)] p-0 m-0 mt-auto sm:m-auto bg-white text-neutral-900 shadow-xl backdrop:bg-black/50 backdrop:backdrop-blur-sm max-h-[90dvh] overflow-y-auto"
      aria-modal="true"
      aria-label="Your order"
    >
      <div className="p-5 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-xl font-bold leading-snug">Your order</h2>
          <button
            ref={closeBtnRef}
            onClick={requestClose}
            aria-label="Close"
            className="shrink-0 rounded-full w-8 h-8 flex items-center justify-center text-neutral-500 hover:bg-neutral-100"
          >
            ×
          </button>
        </div>

        {lines.length === 0 ? (
          <p className="text-sm text-neutral-500 py-4">Your cart is empty.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {lines.map((line) => (
              <li key={line.menuItemId} className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{line.name}</p>
                  <p className="text-sm text-neutral-500">
                    {formatZARCents(line.unitPriceCents * line.quantity)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setQuantity(line.menuItemId, line.quantity - 1)}
                    aria-label={`Decrease quantity of ${line.name}`}
                    className="w-8 h-8 rounded-full border border-neutral-300"
                  >
                    −
                  </button>
                  <span className="w-5 text-center text-sm">{line.quantity}</span>
                  <button
                    onClick={() => setQuantity(line.menuItemId, line.quantity + 1)}
                    aria-label={`Increase quantity of ${line.name}`}
                    className="w-8 h-8 rounded-full border border-neutral-300"
                  >
                    +
                  </button>
                  <button
                    onClick={() => removeItem(line.menuItemId)}
                    aria-label={`Remove ${line.name} from order`}
                    className="w-8 h-8 rounded-full text-neutral-400 hover:text-neutral-700"
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="border-t border-neutral-100 pt-3 flex items-center justify-between font-semibold">
          <span>Subtotal</span>
          <span>{formatZARCents(totalCents)}</span>
        </div>

        {/* CartBar's own aria-live region goes inert while this dialog is open
            (showModal() makes everything outside it inert per the HTML spec) —
            mirror subtotal changes here too so quantity/remove edits are announced. */}
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          Subtotal {formatZARCents(totalCents)}
        </span>

        {belowMinimum && (
          <p className="text-xs text-amber-600">
            Minimum order is R2.00 — add {formatZARCents(MIN_ORDER_CENTS - totalCents)} more to checkout.
          </p>
        )}

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-neutral-600">Table number (optional)</span>
          <input
            type="text"
            value={tableLabel}
            onChange={(e) => setTableLabel(e.target.value)}
            maxLength={32}
            className="rounded-lg border border-neutral-300 px-3 py-2 text-sm"
            placeholder="e.g. 12"
          />
        </label>

        {error && (
          <p role="alert" className="text-xs text-red-600">
            {error}
          </p>
        )}

        <button
          disabled={lines.length === 0 || belowMinimum || isSubmitting}
          title={belowMinimum ? 'Minimum order is R2.00' : undefined}
          onClick={() => void checkout()}
          className="w-full rounded-[var(--chip-radius)] bg-brand-accent text-[var(--brand-accent-text)] py-3 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Checkout
        </button>
      </div>
    </dialog>
  )
}
